import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Car, Navigation, CheckCircle, Megaphone } from 'lucide-react';
import { getPendingRides, getRides, assignDriver, resolveRide, deleteRide, getRideStats } from '../../services/api';
import { isAdminUser } from '../../utils/adminUser';
import useWebSocket from '../../hooks/useWebSocket';
import ActiveOrdersTable from './ActiveOrdersTable';
import AssignDriverModal from './AssignDriverModal';
import { toast } from 'sonner';
import { getRideDestination } from '../../utils/rideFormat';
import { getRidePriceLek } from '../../utils/ridePrice';

const normalizeRideForTable = (ride) => {
  const priceLek = getRidePriceLek(ride);
  return {
    ...ride,
    ticket_number: ride.ticket_number || ride.ride_number || `#${String(ride.id || '').slice(-6).toUpperCase()}`,
    destination: getRideDestination(ride),
    price_lek: priceLek,
    price_eur: ride.price_eur ?? (priceLek != null ? priceLek / 100 : null),
    vehicle_plate: ride.vehicle_plate || ride.vehicle?.plate,
    passenger_count: ride.passenger_count || ride.passengers_count || 1,
  };
};

export const RidesList = ({ fullWidth = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [rideToAssign, setRideToAssign] = useState(null);
  const [rideStats, setRideStats] = useState(null);
  const adminCanDelete = isAdminUser();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = searchParams.get('status');
      let ridesData;

      if (searchParams.get('source') === 'google_ads') {
        ridesData = await getRides({ ad_channel: 'google_ads', limit: 500 });
      } else if (statusParam === 'pending') {
        ridesData = await getPendingRides();
      } else if (statusParam === 'active') {
        ridesData = await getRides({ status: 'pending,accepted,arriving,in_progress' });
      } else if (statusParam && statusParam !== 'all') {
        ridesData = await getRides({ status: statusParam });
      } else {
        ridesData = await getRides({ limit: 200 });
      }

      setRides(ridesData);

      getRideStats()
        .then(setRideStats)
        .catch((error) => console.error('Error fetching ride stats:', error));
    } catch (error) {
      console.error('Error fetching rides:', error);
      toast.error('Gabim në ngarkimin e porosive');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useWebSocket('dispatcher', (message) => {
    if (
      message.type === 'new_ride'
      || message.type === 'ride_updated'
      || message.type === 'ride_assigned'
      || message.type === 'ride_deleted'
    ) {
      fetchData();
    }
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const normalizedRides = useMemo(
    () => rides.map(normalizeRideForTable),
    [rides]
  );

  const stats = useMemo(() => {
    const activeStatuses = ['accepted', 'arriving', 'in_progress', 'assigned', 'en_route'];
    if (rideStats?.by_status) {
      const count = (status) => rideStats.by_status[status] || 0;
      return {
        pending: count('pending'),
        active: activeStatuses.reduce((sum, status) => sum + count(status), 0),
        completed: count('completed'),
        total: rideStats.total,
      };
    }
    return {
      pending: normalizedRides.filter(r => r.status === 'pending').length,
      active: normalizedRides.filter(r => activeStatuses.includes(r.status)).length,
      completed: normalizedRides.filter(r => r.status === 'completed').length,
      total: normalizedRides.length,
    };
  }, [rideStats, normalizedRides]);

  const googleAdsStats = rideStats?.google_ads;
  const isGoogleAdsView = searchParams.get('source') === 'google_ads';

  const handleManualAssign = async (rideId, driverId) => {
    try {
      await assignDriver(rideId, driverId);
      toast.success('Shoferi u caktua me sukses!');
      setShowAssignModal(false);
      setRideToAssign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në caktimin e shoferit');
    }
  };

  const handleCancelRide = async (rideId) => {
    const reason = window.prompt('Arsyeja e anulimit (e detyrueshme):');
    if (!reason?.trim()) return;
    try {
      await resolveRide(rideId, { action: 'cancel', reason: reason.trim() });
      toast.success('Udhëtimi u anulua');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anulimi dështoi');
    }
  };

  const handleCompleteRide = async (rideId) => {
    const ride = normalizedRides.find(r => r.id === rideId);
    if (!window.confirm('Përfundoni këtë udhëtim manualisht?')) return;
    try {
      await resolveRide(rideId, {
        action: 'complete',
        amountReceived: ride?.price_lek || ride?.estimated_price || ride?.fare,
      });
      toast.success('Udhëtimi u përfundua');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Përfundimi dështoi');
    }
  };

  const openAssignModal = (ride) => {
    setRideToAssign(ride);
    setShowAssignModal(true);
  };

  const handleDeleteRide = async (rideId) => {
    const ride = normalizedRides.find((r) => r.id === rideId);
    const label = ride?.ticket_number || ride?.ride_number || rideId.slice(0, 8);
    if (
      !window.confirm(
        `Fshini përgjithmonë porosinë ${label}?\n\nKy veprim nuk mund të kthehet mbrapsht.`
      )
    ) {
      return;
    }
    try {
      await deleteRide(rideId);
      toast.success('Porosia u fshi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fshirja dështoi');
    }
  };

  return (
    <div className={`space-y-4 ${fullWidth ? 'w-full' : 'w-full'}`}>
      {/* Stats Bar */}
      <div className={`grid grid-cols-2 gap-3 ${googleAdsStats ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-xs text-gray-500">Në Pritje</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Navigation className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
            <div className="text-xs text-gray-500">Aktive</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-xs text-gray-500">Përfunduar</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <Car className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
            <div className="text-xs text-gray-500">Gjithsej</div>
          </div>
        </div>
        {googleAdsStats && (
          <button
            type="button"
            onClick={() => navigate(isGoogleAdsView ? '/admin/rides' : '/admin/rides?source=google_ads')}
            className={`bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3 text-left hover:border-amber-400 hover:shadow-md transition ${isGoogleAdsView ? 'ring-2 ring-amber-400' : ''}`}
            title={isGoogleAdsView ? 'Shfaq të gjitha porositë' : 'Shfaq porositë nga Google Ads'}
          >
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600">{googleAdsStats.total}</div>
              <div className="text-xs text-gray-500">
                Nga Google Ads (nga {stats.total} gjithsej) · {googleAdsStats.completed} përfunduar
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Orders Table */}
      <ActiveOrdersTable
        rides={normalizedRides}
        loading={loading}
        title={isGoogleAdsView ? 'Porositë nga Google Ads' : 'Porositë'}
        defaultStatusFilter={searchParams.get('status') || 'all'}
        showAutoAssign={false}
        queueHasDrivers={false}
        onRefresh={fetchData}
        onAssign={openAssignModal}
        onCancel={handleCancelRide}
        onComplete={handleCompleteRide}
        onViewDetails={(ride) => navigate(`/admin/rides/${ride.id}`)}
        onDelete={adminCanDelete ? handleDeleteRide : undefined}
        showDelete={adminCanDelete}
      />

      <AssignDriverModal
        isOpen={showAssignModal}
        onClose={() => { setShowAssignModal(false); setRideToAssign(null); }}
        ride={rideToAssign}
        onAssign={handleManualAssign}
        queueDrivers={[]}
      />
    </div>
  );
};

export default RidesList;
